// @flow

import React, { useEffect, useMemo, useRef, useState } from "react";
import { localize } from "redux-i18n";
import actionCreators from "@modules/client/actionCreator";
import * as clientsSelectors from "@modules/client/selectors";
import { connect } from "react-redux";
import { Button, TooltipDropdown, ConfirmationModal, Text, View, SchemaForm } from "@components";
import type { Client as ClientType } from "@modules/client/types";
import { cloneObject } from "@utils/object";

type OwnProps = $Exact<{
  clientId: number,
  client: ClientType,
  schema: Object,
  uiSchema: Object,
  disableEdit?: boolean,
  isBenchmarking?: boolean,
}>;

type PropsFromDispatch = $Exact<{
  updateClient: typeof actionCreators.UpdateClientDetails,
}>;

type Props = $Exact<{
  ...OwnProps,
  ...PropsFromDispatch,
  ...PropsFromLocalize,
}>;

const DetailsForm = (props: Props) => {
  const formRef = useRef(null);
  const [isEdit, setIsEdit] = useState(false);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [formData, setFormData] = useState(null);

  useEffect(() => {
    if (props.client) {
      setFormData(transformData(props.client));
    }
  }, [props.client?.id]);

  const toggleEdit = () => {
    setIsEdit(prev => !prev);
  };

  const toggleConfirmationModal = () => {
    setConfirmModalOpen(prev => !prev);
  };

  const transformData = (client: ClientType) => {
    if (!Object.keys(client).length) {
      return {};
    }
    const clonedFormdata = cloneObject(client);
    const transformedFormdata = {
      ...clonedFormdata,
      country: clonedFormdata.address.country,
      region: clonedFormdata.address.region,
    };

    delete transformedFormdata.address.country;
    delete transformedFormdata.address.region;
    return transformedFormdata;
  };

  const transformSubmittedData = (data: ClientType) => {
    if (!Object.keys(data).length) {
      return {};
    }

    const clonedFormdata = cloneObject(data);
    const transformedFormdata = {
      ...clonedFormdata,
      address: {
        ...clonedFormdata.address,
        country: clonedFormdata.country,
        region: clonedFormdata.region,
      },
    };

    delete transformedFormdata.country;
    delete transformedFormdata.region;
    return transformedFormdata;
  };

  const submitHandler = (formData: Object) => {
    props.updateClient({ ...props.client, ...transformSubmittedData(formData) });
    toggleEdit();
  };

  const submitForm = () => {
    formRef.current.submit();
  };

  const handleChange = (data: Object) => {
    if (data.status === "INACTIVE" && formData.status === "ACTIVE") {
      toggleConfirmationModal();
      return;
    }

    setFormData(data);
  };

  const submitConfirmationModal = () => {
    setFormData({
      ...formData,
      status: "INACTIVE",
      inTesting: false,
    });
    toggleConfirmationModal();
  };

  const cancelEdit = () => {
    setFormData(transformData(props.client));
    setIsEdit(false);
  };

  const schema = useMemo(() => {
    const { schema } = props;
    if (!schema || !formData) {
      return {};
    }

    const managerOptions = schema.properties.accountManager.properties.id.oneOf;
    const resellerOptions = schema.properties.reseller.properties.id.oneOf;
    return {
      ...schema,
      properties: {
        ...schema.properties,
        accountManager: {
          ...schema.properties.accountManager,
          properties: {
            ...schema.properties.accountManager.properties,
            id: {
              ...schema.properties.accountManager.properties.id,
              title: props.t("MANAGER"),
              oneOf: props.disableEdit
                ? [{ title: formData?.accountManager?.name, const: formData?.accountManager?.id }]
                : managerOptions,
            },
          },
        },
        reseller: {
          ...schema.properties.reseller,
          properties: {
            ...schema.properties.reseller.properties,
            id: {
              ...schema.properties.reseller.properties.id,
              title: props.t("OWNER"),
              oneOf: props.disableEdit
                ? [{ title: formData?.reseller?.name, const: formData?.reseller?.id }]
                : resellerOptions,
            },
          },
        },
      },
    };
  }, [formData, props.schema]);

  return (
    <>
      <View direction="column" flex={1} className="gs-container" gap="6">
        <View direction="row" justifyContent="space-between">
          {!props.isBenchmarking ? (
            <TooltipDropdown
              title={props.t("CLIENT_DETAILS_SECTION_TITLE")}
              description={props.t("CLIENT_DETAILS_SECTION_DESCRIPTION")}
              id="client-details-section"
            >
              <Text type="h6">{props.t("CLIENT_DETAILS")}</Text>
            </TooltipDropdown>
          ) : (
            <Text type="h6">{props.t("BENCHMARKING_CLASSIFICATIONS")}</Text>
          )}
          {!props.disableEdit && (
            <View direction="row" justifyContent="space-between" gap="4">
              {!isEdit ? (
                <Button materialIcon="edit" onClick={toggleEdit} size="small">
                  {props.t("EDIT")}
                </Button>
              ) : (
                <View direction="row" gap="4">
                  <Button color="secondary" onClick={cancelEdit} size="small">
                    {props.t("CANCEL")}
                  </Button>
                  <Button
                    materialIcon="replay_circle_filled"
                    onClick={submitForm}
                    size="small"
                    color="selected"
                  >
                    {props.t("SUBMIT")}
                  </Button>
                </View>
              )}
            </View>
          )}
        </View>
        {props.client && formData && (
          <SchemaForm
            ref={formRef}
            formData={formData}
            schema={schema}
            uiSchema={props.uiSchema}
            readonly={!isEdit}
            onSubmit={submitHandler}
            onChange={handleChange}
            externalSubmit
            boxGrid
          />
        )}
      </View>
      {confirmModalOpen && (
        <ConfirmationModal
          headerText={props.t("DISABLE_CLIENT")}
          bodyContent={props.t("DISABLE_CLIENT_CONFIRMATION_MESSAGE")}
          confirmBtnLabel={props.t("SUBMIT")}
          confirmBtnIcon="replay_circle_filled"
          onCancel={toggleConfirmationModal}
          onConfirm={submitConfirmationModal}
        />
      )}
    </>
  );
};

const mapDispatchToProps: PropsFromDispatch = {
  updateClient: actionCreators.UpdateClientDetails,
};

export default connect(null, mapDispatchToProps)(localize()(DetailsForm));
